import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProtectedResourceDetail } from '../services/api';
import { AppBar, Box, Button, Grid, Paper, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SettingsIcon from '@mui/icons-material/Settings';
import EventIcon from '@mui/icons-material/Event';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import HistoryIcon from '@mui/icons-material/History';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useNavigate } from 'react-router-dom';
import IDComponent from './ID';
import UserSetting from './UserSetting';
import CouponList from './CouponList';
import AnnouncementList from './AnnouncementList';
import PointHistory from './PointHistory';
import StampCard from './StampCard';
import EventList, { EventItem } from './EventList';
import EventDetail from './EventDetail';
import InactiveUserMessage from './InactiveUserMessage';

interface UserInfo {
  name: string;
  email: string;
  id: string;
  rank: number;
  points: number;
  times: number;
  is_staff: boolean;
}

const estimateRank = (points: number | undefined, times: number | undefined): number => {
  var rank = 0;

  if (points !== undefined && times !== undefined) {
    const sum = points + times
    if (sum >= 10) {
      rank = 1;
    } 
    if (sum >= 15) {
      rank = 2;
    } 
    if (sum >= 30) {
      rank = 3;
    } 
    if (sum >= 45) {
      rank = 4;
    } 
    if (sum >= 55) {
      rank = 5;
    } 
    if (sum >= 100) {
      rank = 6;
    }
  }
  return rank;
}

interface HomeProps {
  initialPageGuide?: 'qr' | 'setting' | 'coupons' | 'announcements' | 'event' | 'eventDetail' | 'history' | 'stamp';
}

const Home: React.FC<HomeProps> = ({ initialPageGuide }) => {
  const navigate = useNavigate();
  const { token, logout, isStaff, setIsStaff, isActive, setIsActive, refreshTokenFunction } = useAuth();
  const [message, setMessage] = useState<UserInfo>();
  const [pageGuide, setPageGuide] = useState<'qr' | 'setting' | 'coupons' | 'announcements' | 'event' | 'eventDetail' | 'history' | 'stamp'>(initialPageGuide ?? 'qr');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);

  const seacret_key = "moka";

  useEffect(() => {
    const fetchProtectedResource = async () => {
      // まずlocalStorageからキャッシュされたユーザー情報を読み込む
      const cachedUserInfo = localStorage.getItem('userInfo');
      if (cachedUserInfo) {
        try {
          const parsedInfo = JSON.parse(cachedUserInfo);
          setMessage(parsedInfo);
          setIsStaff(parsedInfo.is_staff || false);
          setIsActive(parsedInfo.is_active !== false); // デフォルトはtrue
        } catch (e) {
          console.error('Failed to parse cached user info:', e);
        }
      }

      if (token) {
        try {
          const response = await getProtectedResourceDetail(token);
          setIsStaff(response.data.is_staff);
          setIsActive(response.data.is_active);
          setMessage(response.data);
          
          // ユーザー情報をlocalStorageに保存（オフライン時用）
          localStorage.setItem('userInfo', JSON.stringify(response.data));
        } catch (error: any) {
          // 401の場合はリフレッシュトークンで再取得を試みる
          if (error.response && error.response.status === 401) {
            try {
              const newToken = await refreshTokenFunction();
              if (newToken) {
                const retryResponse = await getProtectedResourceDetail(newToken);
                setIsStaff(retryResponse.data.is_staff);
                setIsActive(retryResponse.data.is_active);
                setMessage(retryResponse.data);
                
                // ユーザー情報をlocalStorageに保存
                localStorage.setItem('userInfo', JSON.stringify(retryResponse.data));
                return;
              }
            } catch (refreshError) {
              // リフレッシュ失敗時もキャッシュがあればそのまま続行
              console.error('Refresh failed, using cached data:', refreshError);
            }
          } else {
            // ネットワークエラー等の場合、キャッシュがあればそのまま続行
            console.error('Failed to fetch user info, using cached data:', error);
          }
          
          // キャッシュがない場合のみログアウト
          if (!cachedUserInfo) {
            alert('エラーが発生しました。再度ログインしてください。');
            logout();
          }
        }
      }
    };
    fetchProtectedResource();
  }, [token]);

  const pageHandler = (page: string) => {
    if (page === 'event') {
      if (!isActive) {
        return <InactiveUserMessage type="event" />;
      }
      return (
        <EventList
          onSelect={(event) => {
            setSelectedEvent(event);
            setPageGuide('eventDetail');
          }}
        />
      );
    }
    if (page === 'eventDetail') {
      if (!isActive) {
        setPageGuide('event');
        return null;
      }
      return (
        <EventDetail
          event={selectedEvent}
          onBack={() => {
            setPageGuide('event');
            setSelectedEvent(null);
          }}
        />
      );
    }
    if (page === 'qr') {
      if (!isActive) {
        return <InactiveUserMessage type="qr" />;
      }
      return (
        <IDComponent
          message={message}
          estimateRank={estimateRank}
          seacret_key={seacret_key}
        />
      );
    } else if (page === 'setting') {
      return (
        <UserSetting
          message={message}
          estimateRank={estimateRank}
          seacret_key={seacret_key}
          onChangePageGuide={(next) => {
            setPageGuide(next);
          }}
        />
      );
    } else if (page === 'coupons') {
      return <CouponList />;
    } else if (page === 'announcements') {
      return <AnnouncementList />;
    } else if (page === 'history') {
      if (!isActive) {
        return <InactiveUserMessage type="history" />;
      }
      return <PointHistory />;
    } else if (page === 'stamp') {
      if (!isActive) {
        return <InactiveUserMessage type="stamp" />;
      }
      return <StampCard />;
    }
  };

  if (!isStaff) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          pb: { xs: 8, sm: 10 },
          pt: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          background: 'none', // 背景削除
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
            maxWidth: 600,
            mx: 'auto',
            px: 0, // ← 余白を消す
            mt: { xs: 1, sm: 2 },
          }}
        >
          {pageHandler(pageGuide)}
        </Box>
        <Paper
          elevation={10}
          sx={{
            position: 'fixed',
            left: 0,
            bottom: 0,
            width: '100%',
            maxWidth: '100%',
            borderRadius: 0,
            zIndex: 1201,
            boxShadow: '0 -2px 16px rgba(33, 150, 243, 0.08)',
            background: 'linear-gradient(90deg, #e3f2fd 60%, #90caf9 100%)',
            borderTop: '2px solid #1976d2',
            py: 0.5,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center" sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
            <Grid item xs={2}>
              <Button
                onClick={() => {
                  if (!isActive) {
                    alert('新制度への移行が必要です。アクティベーションコードを購入して入力してください。');
                    return;
                  }
                  setSelectedEvent(null);
                  setPageGuide('event');
                }}
                disabled={!isActive}
                sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  py: 1, minWidth: 0, color: pageGuide === 'event' ? '#1976d2' : '#1565c0',
                  background: pageGuide === 'event' ? 'rgba(144,202,249,0.18)' : 'transparent',
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 10,
                  opacity: !isActive ? 0.5 : 1,
                }}
              >
                <EventIcon sx={{ color: pageGuide === 'event' ? '#1976d2' : '#1565c0', mb: 0.2, fontSize: 20 }} />
                イベント
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                onClick={() => setPageGuide('coupons')}
                sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  py: 1, minWidth: 0, color: pageGuide === 'coupons' ? '#1976d2' : '#1565c0',
                  background: pageGuide === 'coupons' ? 'rgba(144,202,249,0.18)' : 'transparent',
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 10,
                }}
              >
                <CardGiftcardIcon sx={{ color: pageGuide === 'coupons' ? '#1976d2' : '#1565c0', mb: 0.2, fontSize: 20 }} />
                特典
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                onClick={() => {
                  if (!isActive) {
                    alert('新制度への移行が必要です。アクティベーションコードを購入して入力してください。');
                    return;
                  }
                  setPageGuide('stamp');
                }}
                disabled={!isActive}
                sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  py: 1, minWidth: 0, color: pageGuide === 'stamp' ? '#1976d2' : '#1565c0',
                  background: pageGuide === 'stamp' ? 'rgba(144,202,249,0.18)' : 'transparent',
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 10,
                  opacity: !isActive ? 0.5 : 1,
                }}
              >
                <BookmarkIcon sx={{ color: pageGuide === 'stamp' ? '#1976d2' : '#1565c0', mb: 0.2, fontSize: 20 }} />
                スタンプ
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                onClick={() => {
                  if (!isActive) {
                    alert('新制度への移行が必要です。アクティベーションコードを購入して入力してください。');
                    return;
                  }
                  setPageGuide('qr');
                }}
                disabled={!isActive}
                sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  py: 1, minWidth: 0, color: pageGuide === 'qr' ? '#1976d2' : '#1565c0',
                  background: pageGuide === 'qr' ? 'rgba(144,202,249,0.18)' : 'transparent',
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 10,
                  opacity: !isActive ? 0.5 : 1,
                }}
              >
                <QrCodeIcon sx={{ color: pageGuide === 'qr' ? '#1976d2' : '#1565c0', mb: 0.2, fontSize: 20 }} />
                会員証
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                onClick={() => setPageGuide('announcements')}
                sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  py: 1, minWidth: 0, color: pageGuide === 'announcements' ? '#1976d2' : '#1565c0',
                  background: pageGuide === 'announcements' ? 'rgba(144,202,249,0.18)' : 'transparent',
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 10,
                }}
              >
                <AnnouncementIcon sx={{ color: pageGuide === 'announcements' ? '#1976d2' : '#1565c0', mb: 0.2, fontSize: 20 }} />
                お知らせ
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                onClick={() => setPageGuide('setting')}
                sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  py: 1, minWidth: 0, color: pageGuide === 'setting' ? '#1976d2' : '#1565c0',
                  background: pageGuide === 'setting' ? 'rgba(144,202,249,0.18)' : 'transparent',
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 10,
                }}
              >
                <SettingsIcon sx={{ color: pageGuide === 'setting' ? '#1976d2' : '#1565c0', mb: 0.2, fontSize: 20 }} />
                設定
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  } else {
    return (
      <>
        {navigate('/staff-dashboard')}
      </>
    )
  }
};

export default Home;
